import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { platformApiService } from "../services/platformApi";
import { IComponentNode } from "../types/pageBuilder.types";

interface PlanOption {
  value: string;
  label: string;
  description?: string;
  price?: string;
  features?: string[];
}

/**
 * Extract plan data from pricing zone component structure
 */
const extractPlanFromNode = (node: IComponentNode, language: string = "en"): PlanOption | null => {
  if (!node || node.type !== "container") return null;

  // Map plan IDs to values (by name)
  const planIdToValue: Record<string, string> = {
    "plan-trial": "",
    "plan-basic": "basic-plan",
    "plan-pro": "pro-plan",
  };

  const planId = node.id;
  if (!planId || !planIdToValue.hasOwnProperty(planId)) return null;

  const value = planIdToValue[planId];

  // Helper to find a child node by ID pattern
  const findNodeById = (parent: IComponentNode, idPattern: string): IComponentNode | null => {
    if (parent.id?.includes(idPattern)) return parent;
    if (parent.children) {
      for (const child of parent.children) {
        const found = findNodeById(child, idPattern);
        if (found) return found;
      }
    }
    return null;
  };

  // Extract plan details
  const titleNode = findNodeById(node, "title");
  const descNode = findNodeById(node, "desc");
  const priceNode = findNodeById(node, "price");
  const periodNode = findNodeById(node, "period");
  const featuresNode = findNodeById(node, "features");

  // Helper to get translated text from component node
  const getComponentText = (node: IComponentNode | null, language: string): string => {
    if (!node) return "";
    // Check for translations in component.translations.props.text[language]
    const translatedText = (node as any).translations?.props?.text?.[language];
    if (translatedText) return translatedText;
    // Fallback to props.text
    return node.props?.text || "";
  };

  const label = titleNode ? getComponentText(titleNode, language) : "";
  const description = descNode ? getComponentText(descNode, language) : "";

  let price = "";
  if (priceNode) {
    const priceText = getComponentText(priceNode, language);
    const periodText = periodNode ? getComponentText(periodNode, language) : "";
    price = priceText + (periodText ? ` ${periodText}` : "");
  }

  const features: string[] = [];
  if (featuresNode?.children) {
    featuresNode.children.forEach((featureNode) => {
      const featureText = getComponentText(featureNode, language);
      // Remove checkmark symbols
      const cleanText = featureText.replace(/^[✓✔]/g, "").trim();
      if (cleanText) features.push(cleanText);
    });
  }

  return {
    value,
    label,
    description,
    price: price || undefined,
    features: features.length > 0 ? features : undefined,
  };
};

/**
 * Hook to extract plans from pricing zone
 */
export const usePricingPlans = (): { plans: PlanOption[]; loading: boolean } => {
  const { i18n } = useTranslation();
  const [plans, setPlans] = useState<PlanOption[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const loadPlans = async () => {
      try {
        setLoading(true);
        const response = await platformApiService.getPlatformZone("pricing", "plans");
        const zoneContent = response.data?.zone;

        if (!zoneContent || !zoneContent.children) {
          setPlans([]);
          return;
        }

        // Find the plans grid
        const plansGrid = zoneContent.children.find((child: IComponentNode) => child.id === "plans-grid");
        if (!plansGrid || !plansGrid.children) {
          setPlans([]);
          return;
        }

        // Get current language from i18n
        const language = (i18n.language || "en").split("-")[0] as "en" | "de" | "ar";

        // Extract plans from grid children
        const extractedPlans: PlanOption[] = [];
        plansGrid.children.forEach((planNode: IComponentNode) => {
          const plan = extractPlanFromNode(planNode, language);
          if (plan) extractedPlans.push(plan);
        });

        setPlans(extractedPlans);
      } catch (error) {
        console.error("Error loading pricing plans:", error);
        setPlans([]);
      } finally {
        setLoading(false);
      }
    };

    loadPlans();
  }, [i18n.language]);

  return { plans, loading };
};
